import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, ChevronLeft, Clock, Hourglass, Images, Inbox, MessageSquare, Pencil, Plus, User } from "lucide-react";
import { cronIsPaused, runJobName, type AssetItem, type AutomationRun, type CronJob, type CronManageResult } from "@timibot/core";
import { useApp } from "../../context";
import { useObserve } from "../../state/observable";
import { loadCustomRoles, presetRoles, rolePrompt, roleIcons, saveCustomRoles, type Role } from "../../state/roles";
import { CircleIconButton, Dialog, Menu, PlainRow, SectionTitle, Spinner, useLongPress, useMenu } from "../../ui/components";
import { AssetDetail, AssetThumb, AssetsView } from "./Assets";
import { CreateCronSheet, CronActions, CronRow, CronRunsSheet, cronSorted, willCatchUp, catchUpMessage } from "./Cron";
import { RoleEditor } from "./RoleEditor";
import { BotScreenCard } from "../screen/BotScreenCard";
import "./workspace.css";

type Page = { type: "home" } | { type: "assets" } | { type: "asset"; item: AssetItem } | { type: "results" };

/**
 * 底部导航“工作台”：一页看完，不分标签（对应 iOS 的 WorkspaceTab）。
 * 1. 角色：选一个角色就开始一段对话
 * 2. 长期目标：定时任务最近每次运行做了什么，点开就是那次运行的对话
 * 3. 自动化：让它定时帮你做的事（Hermes 定时任务）
 * 4. 作品：所有对话里生成的图片、视频、文件
 */
export function WorkspaceTab() {
  const app = useApp();
  const [stack, setStack] = useState<Page[]>([{ type: "home" }]);
  const page = stack[stack.length - 1];
  const push = (p: Page) => setStack((s) => [...s, p]);
  const pop = () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  const title = page.type === "home" ? "工作台" : page.type === "assets" ? "作品" : page.type === "results" ? "长期目标"
    : page.item.kind === "image" ? "图片" : page.item.kind === "video" ? "视频" : page.item.kind === "audio" ? "音频" : "文件";

  // 换了 Bot / 连接：回到首页
  useEffect(() => { setStack([{ type: "home" }]); }, [app.workspaceScope]);

  return (
    <div className="workspace">
      <header className="workspace-header">
        {stack.length > 1 ? <button className="back-button" onClick={pop}><ChevronLeft size={22} />返回</button> : <span />}
        <span className="t-headline">{title}</span>
        <span />
      </header>
      <div className="workspace-scroll scroll" key={stack.length}>
        <div className="workspace-column">
          {page.type === "home" && <Home push={push} />}
          {page.type === "assets" && <AssetsView onOpen={(item) => push({ type: "asset", item })} />}
          {page.type === "asset" && <AssetDetail item={page.item} onOpen={(item) => push({ type: "asset", item })} />}
          {page.type === "results" && <ResultsView />}
        </div>
      </div>
    </div>
  );
}

function Home({ push }: { push: (p: Page) => void }) {
  const app = useApp();
  useObserve(app.assets, app.automationFeed);
  const [customRoles, setCustomRoles] = useState<Role[]>(loadCustomRoles);
  const [editingRole, setEditingRole] = useState<Role>();
  const [cron, setCron] = useState<CronManageResult>();
  const [cronError, setCronError] = useState<string>();
  const [showCreate, setShowCreate] = useState(false);
  const [runsFor, setRunsFor] = useState<CronJob>();
  const [deleting, setDeleting] = useState<CronJob>();
  const [resuming, setResuming] = useState<CronJob>();
  const lastLoaded = useRef<{ scope: string; at: number }>(undefined);
  const roleMenu = useMenu();
  const [menuRole, setMenuRole] = useState<Role>();

  const loadCron = async () => {
    if (!app.api) return;
    try {
      setCron(await app.api.listCronJobs(app.selectedProfile));
      setCronError(undefined);
    } catch (e) {
      setCronError(e instanceof Error ? e.message : String(e));
    }
  };

  const reload = async () => {
    await Promise.all([
      app.refreshAssets(),
      loadCron(),
      app.automationFeed.refresh(app.api, app.selectedProfile, app.workspaceScope),
    ]);
  };

  // 切换底部导航回来时不每次都重新拉，同一个 Bot 30 秒内用上次的
  useEffect(() => {
    const scope = app.workspaceScope;
    const last = lastLoaded.current;
    if (last && last.scope === scope && Date.now() - last.at < 30000) return;
    if (last?.scope !== scope) { setCron(undefined); setCronError(undefined); }
    lastLoaded.current = { scope, at: Date.now() };
    void reload();
  }, [app.workspaceScope, app.connection.kind === "connected"]); // eslint-disable-line react-hooks/exhaustive-deps

  // 定时任务有变化：任务和结果列表都刷新
  useEffect(() => {
    if (app.cronRevision === 0) return;
    void loadCron();
    void app.automationFeed.refresh(app.api, app.selectedProfile, app.workspaceScope);
  }, [app.cronRevision]); // eslint-disable-line react-hooks/exhaustive-deps

  /** 先在界面上切过去（经中转往返要一两秒），服务器拒绝了再切回来 */
  const setPaused = (id: string, paused: boolean) => setCron((c) => c && {
    ...c, jobs: c.jobs?.map((j) => j.job_id === id ? { ...j, enabled: !paused, state: paused ? "paused" : "scheduled" } : j),
  });
  const toggleNow = (job: CronJob) => {
    setPaused(job.job_id, !cronIsPaused(job));
    void CronActions.toggle(job, app).then((ok) => { if (!ok) setPaused(job.job_id, cronIsPaused(job)); });
  };
  const toggle = (job: CronJob) => { if (willCatchUp(job)) setResuming(job); else toggleNow(job); };

  const roles = [...presetRoles, ...customRoles];
  const jobs = cronSorted(cron?.jobs ?? []);
  const feed = app.automationFeed;
  const owner = !app.isGuest;
  const recentAssets = app.assets.assets.slice(0, 6);

  return (
    <div className="workspace-home">
      {!app.isDemo && <BotScreenCard />}
      <section>
        <SectionTitle title="角色" />
        <div className="role-strip">
          {roles.map((role) => (
            <RoleCard key={role.id} role={role} onStart={() => app.startRole(role.name, rolePrompt(role))}
              onMenu={(pos) => { setMenuRole(role); roleMenu.show(pos); }} />
          ))}
          <button className="role-card role-add" onClick={() => setEditingRole({ id: crypto.randomUUID(), emoji: "", name: "", intro: "", persona: "", isCustom: true, symbol: "person" })}>
            <span className="role-avatar"><Plus size={17} /></span>
            <span className="t-subheadline medium">自定义</span>
          </button>
        </div>
      </section>

      {(feed.runs.length > 0 || jobs.length > 0) && (
        <section>
          <SectionTitle title="长期目标" trailing={feed.runs.length > 5 ? <button className="t-footnote t-2" onClick={() => push({ type: "results" })}>全部</button> : undefined} />
          {feed.loaded && feed.runs.length === 0 && <PlainRow icon={<Inbox size={15} />} title="还没有运行过" subtitle="自动化每次运行做了什么，会列在这里" />}
          {feed.runs.slice(0, 5).map((run) => <ResultRow key={run.id} run={run} preview={feed.previews[run.id]} />)}
        </section>
      )}

      <section>
        <SectionTitle title="自动化" trailing={<CircleIconButton icon={<Plus size={16} strokeWidth={2.4} />} label="新建自动化" onClick={() => setShowCreate(true)} />} />
        {cronError && <div className="t-footnote t-danger ws-pad">{cronError}</div>}
        {cron?.gateway_running === false && <div className="t-caption ws-pad" style={{ color: "var(--warning)" }}>服务器网关没在运行，暂时不会触发</div>}
        {cron && jobs.length === 0 && <PlainRow icon={<Clock size={15} />} title="还没有自动化" subtitle="让它每天按时帮你做事" onClick={() => setShowCreate(true)} />}
        {jobs.map((job) => (
          <CronRow key={job.job_id} job={job}
            onToggle={owner ? () => toggle(job) : undefined}
            onOpen={() => setRunsFor(job)}
            onRunNow={owner ? () => void CronActions.runNow(job, app) : undefined}
            onDelete={owner ? () => setDeleting(job) : undefined} />
        ))}
        {!cron && !cronError && <div className="center-pad"><Spinner /></div>}
      </section>

      <section>
        <SectionTitle title="作品" trailing={app.assets.assets.length ? <button className="t-footnote t-2" onClick={() => push({ type: "assets" })}>全部 {app.assets.assets.length}</button> : undefined} />
        {recentAssets.length === 0 ? (
          <PlainRow icon={<Images size={15} />} title={app.assets.loaded ? "还没有作品" : "正在整理…"} subtitle={app.assets.loaded ? "生成的图片、视频、文件会出现在这里" : undefined} />
        ) : (
          <div className="asset-grid ws-pad16">
            {recentAssets.map((item) => (
              <button key={item.source} className="asset-cell" onClick={() => push({ type: "asset", item })}><AssetThumb item={item} /></button>
            ))}
          </div>
        )}
      </section>

      <Menu open={roleMenu.open} anchor={roleMenu.anchor} onClose={roleMenu.close} align="start" width={180} items={[
        { label: "开始对话", icon: <MessageSquare size={16} />, onSelect: () => { if (menuRole) app.startRole(menuRole.name, rolePrompt(menuRole)); } },
        ...(menuRole?.isCustom ? [{ label: "编辑", icon: <Pencil size={16} />, onSelect: () => setEditingRole(menuRole) }] : []),
      ]} />
      <RoleEditor role={editingRole} onClose={() => setEditingRole(undefined)} onSave={(saved, original) => {
        const next = customRoles.filter((r) => r.id !== original.id);
        if (saved) next.push(saved);
        setCustomRoles(next);
        saveCustomRoles(next);
      }} />
      <CreateCronSheet open={showCreate} onClose={() => setShowCreate(false)} onCreated={loadCron} />
      <CronRunsSheet job={runsFor} onClose={() => setRunsFor(undefined)} />
      <Dialog open={deleting !== undefined} title={`删除“${deleting ? cronName(deleting) : ""}”？`} message="任务会从服务器上删掉，删除后不能恢复。"
        onClose={() => setDeleting(undefined)}
        buttons={[{ title: "取消", role: "cancel" }, { title: "删除", role: "destructive", onClick: () => { if (deleting) void CronActions.remove(deleting, app); } }]} />
      <Dialog open={resuming !== undefined} title="恢复后会马上补跑一次" message={resuming ? catchUpMessage(resuming) : undefined}
        onClose={() => setResuming(undefined)}
        buttons={[{ title: "取消", role: "cancel" }, { title: "恢复", onClick: () => { if (resuming) toggleNow(resuming); } }]} />
    </div>
  );
}

function cronName(j: CronJob): string {
  return j.name || (j.prompt_preview ? [...j.prompt_preview].slice(0, 30).join("") : j.job_id);
}

function RoleCard({ role, onStart, onMenu }: { role: Role; onStart: () => void; onMenu: (pos: { x: number; y: number }) => void }) {
  const press = useLongPress(onMenu);
  return (
    <button className="role-card" onClick={onStart} {...press}>
      <RoleAvatar role={role} size={40} onSurface />
      <span className="spacer" />
      <span className="t-subheadline semibold nowrap">{role.name}</span>
      <span className="t-caption2 t-2 clamp2">{role.intro}</span>
    </button>
  );
}

export function RoleAvatar({ role, size = 44, onSurface }: { role: Role; size?: number; onSurface?: boolean }) {
  const Icon = role.symbol ? roleIcons[role.symbol] ?? User : undefined;
  return (
    <span className="role-avatar" style={{ width: size, height: size, background: onSurface ? "var(--bg)" : "var(--surface)" }}>
      {Icon ? <Icon size={size * 0.42} /> : <span style={{ fontSize: size * 0.5 }}>{role.emoji}</span>}
    </span>
  );
}

/** 长期目标的一行：任务名、什么时候、做了什么（最后一条回复的第一句），点开就是那次运行的对话 */
function ResultRow({ run, preview }: { run: AutomationRun; preview?: string }) {
  const app = useApp();
  const active = run.is_active === true;
  return (
    <button className="result-row" onClick={() => app.openStored(run.id, app.selectedProfile, run.title)}>
      <span className="icon-badge" style={{ width: 34, height: 34, fontSize: 14 }}>{active ? <Hourglass size={14} /> : <Check size={14} />}</span>
      <span className="col" style={{ flex: 1, minWidth: 0, gap: 3 }}>
        <span className="row" style={{ gap: 8 }}>
          <span className="t-subheadline medium nowrap" style={{ flex: 1 }}>{runJobName(run)}</span>
          <span className="t-caption t-3 nowrap">{runWhen(run.started_at)}</span>
        </span>
        <span className={`t-caption t-2 clamp2${!active && preview === undefined ? " placeholder" : ""}`}>{active ? "正在运行…" : (preview ?? "　")}</span>
      </span>
    </button>
  );
}

function runWhen(at?: number): string {
  if (!at) return "";
  const d = new Date(at * 1000);
  const now = new Date();
  const time = d.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (d.getTime() >= startOfToday) return `今天 ${time}`;
  if (d.getTime() >= startOfToday - 86400000) return `昨天 ${time}`;
  return `${d.getMonth() + 1}月${d.getDate()}日 ${time}`;
}

/** 全部长期目标的运行结果（最近 50 次） */
function ResultsView() {
  const app = useApp();
  const feed = app.automationFeed;
  useObserve(feed);
  useEffect(() => { void feed.refresh(app.api, app.selectedProfile, app.workspaceScope, 50); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!app.api || !feed.runs.length) return;
    void feed.loadPreviews(app.api, app.selectedProfile, app.workspaceScope, feed.runs.length);
  }, [feed.runs.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="workspace-list">{feed.runs.map((run) => <ResultRow key={run.id} run={run} preview={feed.previews[run.id]} />)}</div>;
}

export function Section({ children }: { children: ReactNode }) {
  return <section>{children}</section>;
}
